import React, { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { fetchLiveChannels } from '../LiveChannels/service';

const getChannelRanges = (channelCount) => {
  const ranges = [{ key: 'first', label: 'CHANNELS 1-50', min: 1, max: 50 }];

  if (channelCount > 50) {
    ranges.push({
      key: 'second',
      label: channelCount > 100 ? 'CHANNELS 51-100' : 'CHANNELS 50+',
      min: 51,
      max: channelCount > 100 ? 100 : Infinity,
    });
  }

  if (channelCount > 100) {
    ranges.push({ key: 'third', label: 'CHANNELS 100+', min: 101, max: Infinity });
  }

  return ranges;
};

const getChannelNumber = (channel, index) => {
  const value = channel?.channel_number ?? channel?.channel_no ?? channel?.number ?? channel?.position;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : index + 1;
};

const OurChannels = () => {
  const navigate = useNavigate();
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const sessionId = useSelector((state) => state?.sessionId?.value);
  const [channels, setChannels] = useState([]);
  const [selectedRange, setSelectedRange] = useState('first');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Our Channels - Stream Online | Free Live Sports TV';
  }, []);

  useEffect(() => {
    let active = true;
    const appInfo = { projectDetails: projectInfo, accessToken, sessionId };

    const loadChannels = async () => {
      setLoading(true);
      try {
        const response = await fetchLiveChannels(appInfo);
        const channelList = response?.data?.data?.channels || [];
        if (active) {
          setChannels(channelList.filter((channel) => channel?.id && channel?.channel_name));
          setError(false);
        }
      } catch (requestError) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadChannels();
    return () => { active = false; };
  }, [accessToken, projectInfo, sessionId]);

  const numberedChannels = useMemo(
    () => channels.map((channel, index) => ({ ...channel, channelNumber: getChannelNumber(channel, index) })),
    [channels]
  );

  const channelRanges = useMemo(() => getChannelRanges(channels.length), [channels.length]);

  const visibleChannels = numberedChannels.filter((channel) => {
    const range = channelRanges.find(({ key }) => key === selectedRange) || channelRanges[0];
    return channel.channelNumber >= range.min && channel.channelNumber <= range.max;
  });

  const openChannel = (channel) => navigate(`/live-channels?c=${channel.id}`);

  const openChannelInfo = (channel) => {
    const channelId = channel?.id || channel?.channel_id;
    const vanityUrl =
      channel?.vanity_url ||
      channel?.vanityUrl ||
      channel?.channel_name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') ||
      channelId;
    navigate(`/channel/${vanityUrl}`, { state: { channel_id: channelId, channel } });
  };

  return (
    <main className="ourChannelsPage">
      <header className="ourChannelsHeader">
        <h1>OUR CHANNELS</h1>
        <p>Check out the full list of channels currently available to watch on Free Live Sports.</p>
      </header>

      {channelRanges.length > 1 && (
        <nav className={`channelRangeNav channelRangeNav--${channelRanges.length}`} aria-label="Channel ranges">
          {channelRanges.map((range) => (
            <button
              key={range.key}
              className={selectedRange === range.key ? 'isSelected' : ''}
              onClick={() => setSelectedRange(range.key)}
              type="button"
            >
              {range.label}
            </button>
          ))}
        </nav>
      )}

      <section className="ourChannelsContent" aria-live="polite">
        {loading && <p className="channelState">Loading channels...</p>}
        {!loading && error && <p className="channelState">Unable to load channels.</p>}
        {!loading && !error && visibleChannels.length === 0 && <p className="channelState">No channels are available in this range.</p>}
        <div className="ourChannelsGrid">
          {!loading && !error && visibleChannels.map((channel) => (
            <article className="ourChannelCard" key={channel.id}>
              <button className="channelLogoButton" onClick={() => openChannel(channel)} type="button" aria-label={`Watch ${channel.channel_name}`}>
                <img src={channel.logo} alt="" loading="lazy" />
              </button>
              <div className="channelCardDetails">
                <button className="channelNameButton" onClick={() => openChannel(channel)} type="button">
                  {channel.channel_name}
                </button>
                <button className="channelInfoButton" onClick={() => openChannelInfo(channel)} type="button" aria-label={`Information for ${channel.channel_name}`}>
                  <Info size={17} strokeWidth={3} aria-hidden="true" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {channelRanges.length > 1 && (
        <nav className={`channelRangeNav channelRangeNavBottom channelRangeNav--${channelRanges.length}`} aria-label="Channel ranges">
          {channelRanges.map((range) => (
            <button
              key={range.key}
              className={selectedRange === range.key ? 'isSelected' : ''}
              onClick={() => setSelectedRange(range.key)}
              type="button"
            >
              {range.label}
            </button>
          ))}
        </nav>
      )}

      <p className="channelAvailability">Channel availability may vary by location.</p>
    </main>
  );
};

export default OurChannels;
